import { Dialog } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import PropTypes from 'prop-types';
import React from 'react';

// Card 457: "you have Product Improvement credits", shown once per increase.
// Two wordings, chosen by the backend (domain/pip_credits/bl.ts):
//  - welcome: this admin has never been through the PIP's tour;
//  - more: they have, so it leads straight to adding a tour.
// Esc, the backdrop, the close button and "Not now" all close it, and every
// close counts as seen (the host does that).

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1 border-0 cursor-pointer font-semibold text-sm px-4 py-2.5 rounded-xl whitespace-nowrap transition-colors duration-150 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';
const PRIMARY = `${BUTTON_BASE} bg-indigo-600 hover:bg-indigo-700 text-white`;
const GHOST = `${BUTTON_BASE} bg-transparent hover:bg-slate-100 text-slate-600`;

// Beyond this many, the slots are said in words only.
export const MAX_SLOT_MARKS = 12;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The dialog's words for a notice. Pure, so the copy is tested on its own. */
export function pipCreditsCopy(notice, companyName) {
  const { kind, total, available, added } = notice;
  const company = companyName || 'your company';
  if (kind === 'welcome') {
    return {
      title: 'Product Improvement is ready for you',
      body: `TripAdmit has given ${company} ${plural(total, 'Product Improvement credit')}. Each credit analyses one of your tours: we read its reviews and show you what guests love, what lets them down and how you compare with rival tours.`,
      action: 'Take the tour'
    };
  }
  if (Number.isFinite(added) && added > 0) {
    return {
      title: `You've got ${plural(added, 'more Product Improvement credit')}`,
      body: `You now have ${available} of your ${plural(total, 'credit')} free to use. Add a tour and we'll start analysing its reviews straight away.`,
      action: 'Add a tour'
    };
  }
  return {
    title: `You have ${plural(available, 'unused Product Improvement credit')}`,
    body: "Add a tour and we'll start analysing its reviews straight away.",
    action: 'Add a tour'
  };
}

// The cap as a row of slots, used then free, so "3 of 5 free" is seen at a glance.
const SlotMarks = ({ total, used }) => {
  if (total > MAX_SLOT_MARKS) return null;
  return (
    <div className="flex flex-wrap gap-1.5" aria-hidden="true" data-testid="pip-credits-slots">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={
            i < used
              ? 'w-6 h-6 rounded-md bg-indigo-200'
              : 'w-6 h-6 rounded-md bg-white border-2 border-solid border-indigo-500'
          }
        />
      ))}
    </div>
  );
};

SlotMarks.propTypes = { total: PropTypes.number.isRequired, used: PropTypes.number.isRequired };

export function PipCreditsDialog({ open, notice, companyName, onClose, onAction }) {
  if (!notice) return null;
  const copy = pipCreditsCopy(notice, companyName);
  const used = Math.max(0, notice.used || 0);

  // Passing onKeyDown to a Material-UI v4 Dialog replaces the Modal's own
  // handler, which is the one that closes on Escape (see WhatsNewDialog).
  const onKeyDown = event => {
    if (event.key === 'Escape') onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={onKeyDown}
      maxWidth="xs"
      fullWidth
      aria-labelledby="pip-credits-title"
      aria-describedby="pip-credits-body"
      PaperProps={{ style: { borderRadius: 24 }, 'data-testid': 'pip-credits-dialog', 'data-kind': notice.kind }}>
      <div className="relative bg-indigo-50 px-6 pt-6 pb-5">
        <button type="button" onClick={onClose} aria-label="Close" className={`${GHOST} !p-2 absolute top-3 right-3`}>
          <Close fontSize="small" />
        </button>
        <p className="text-sm font-bold text-indigo-900 m-0 mb-3 pr-10">
          {plural(notice.available, 'credit')} free
          {used > 0 ? <span className="font-semibold text-indigo-700">{`, ${used} in use`}</span> : null}
        </p>
        <SlotMarks total={notice.total} used={used} />
      </div>
      <div className="px-6 pt-5">
        <h2 id="pip-credits-title" className="text-xl font-black text-slate-900 m-0">
          {copy.title}
        </h2>
        <p id="pip-credits-body" className="text-sm text-slate-600 leading-relaxed m-0 mt-2">
          {copy.body}
        </p>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-5 flex-wrap">
        <button type="button" className={GHOST} onClick={onClose}>
          Not now
        </button>
        <button type="button" className={PRIMARY} onClick={onAction} autoFocus>
          {copy.action}
        </button>
      </div>
    </Dialog>
  );
}

PipCreditsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  notice: PropTypes.shape({
    kind: PropTypes.oneOf(['welcome', 'more']).isRequired,
    total: PropTypes.number.isRequired,
    used: PropTypes.number,
    available: PropTypes.number.isRequired,
    added: PropTypes.number
  }),
  companyName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired
};

export default PipCreditsDialog;
